import React from "react";
import { Alert, Button, Card, Space, Typography } from "antd";
import {
  CalendarOutlined,
  CloseCircleOutlined,
  HomeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { formatDate } from "../../../uteis/dateUtils";

const { Title, Text, Paragraph } = Typography;

type Props = {
  empreendimento?: string | null;
  periodoProcuradoIni?: string | null;
  periodoProcuradoFim?: string | null;
  periodoEncontradoIni?: string | null;
  periodoEncontradoFim?: string | null;
  loading?: boolean;
  onEncerrarProcura: () => Promise<void> | void;
  onContinuarProcurandoOutroPeriodo: () => Promise<void> | void;
};

function renderPeriodo(inicio?: string | null, fim?: string | null) {
  if (!inicio && !fim) return "—";
  if (inicio && fim) return `${formatDate(inicio)} até ${formatDate(fim)}`;
  return formatDate(inicio ?? fim ?? "");
}

export default function StepSemInteressePeriodo({
  empreendimento,
  periodoProcuradoIni,
  periodoProcuradoFim,
  periodoEncontradoIni,
  periodoEncontradoFim,
  loading = false,
  onEncerrarProcura,
  onContinuarProcurandoOutroPeriodo,
}: Props) {
  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Card
        bordered={false}
        style={{ borderRadius: 16, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <div>
            <Title level={2} style={{ marginBottom: 8 }}>
              Você ainda tem interesse em encontrar uma reserva para o período procurado?
            </Title>
            <Paragraph style={{ marginBottom: 0, fontSize: 16 }}>
              Revise as informações abaixo e escolha como deseja seguir.
            </Paragraph>
          </div>

          <Card
            bordered={false}
            style={{ borderRadius: 14, background: "rgba(0,0,0,0.02)" }}
            styles={{ body: { padding: 18 } }}
          >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <Text type="secondary">
                  <HomeOutlined style={{ marginRight: 8 }} />
                  Empreendimento
                </Text>
                <Text strong>{empreendimento ?? "—"}</Text>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <Text type="secondary">
                  <CalendarOutlined style={{ marginRight: 8 }} />
                  Período procurado
                </Text>
                <Text strong>{renderPeriodo(periodoProcuradoIni, periodoProcuradoFim)}</Text>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <Text type="secondary">
                  <CalendarOutlined style={{ marginRight: 8 }} />
                  Período encontrado
                </Text>
                <Text strong>{renderPeriodo(periodoEncontradoIni, periodoEncontradoFim)}</Text>
              </div>
            </Space>
          </Card>

          <Alert
            type="info"
            showIcon
            message="Selecione uma das opções abaixo"
            description="Escolha se deseja encerrar a procura ou continuar buscando outro período dentro da sua necessidade."
            style={{ borderRadius: 14 }}
          />

          <Card
            bordered={false}
            style={{ borderRadius: 14, background: "rgba(0,0,0,0.02)" }}
            styles={{ body: { padding: 18 } }}
          >
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Title level={4} style={{ margin: 0 }}>
                Não tenho mais interesse no período procurado
              </Title>
              <Text type="secondary">
                Exemplo: desisti da viagem, já reservei outro local ou não quero mais viajar nesse período.
              </Text>
              <Button
                danger
                size="large"
                icon={<CloseCircleOutlined />}
                onClick={onEncerrarProcura}
                loading={loading}
              >
                Encerrar procura
              </Button>
            </Space>
          </Card>

          <Card
            bordered={false}
            style={{ borderRadius: 14, background: "rgba(24,144,255,0.06)" }}
            styles={{ body: { padding: 18 } }}
          >
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Title level={4} style={{ margin: 0 }}>
                Ainda tenho interesse no período procurado, mas não gostei deste período encontrado
              </Title>
              <Text type="secondary">
                Exemplo: continuo procurando disponibilidade dentro do período solicitado, mas as datas encontradas não atendem ao que preciso.
              </Text>
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                onClick={onContinuarProcurandoOutroPeriodo}
                loading={loading}
              >
                Continuar procurando outro período
              </Button>
            </Space>
          </Card>
        </Space>
      </Card>
    </div>
  );
}
